import React, { useState, useRef, useEffect } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Controller } from "react-hook-form";

interface LocationInputProps {
  name: string;
  control?: any;
  onPlaceSelect: (place: google.maps.places.Place | null) => void;
  rules?: any
}

export function LocationInput({
  name,
  control,
  onPlaceSelect,
  rules
}: LocationInputProps) {
  const places = useMapsLibrary("places");
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([]);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const [inputValue, setInputValue] = useState(""); // for uncontrolled fallback

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, []);

  const fetchSuggestions = async (inputValue: string) => {
    if (!inputValue || !places?.AutocompleteSuggestion) {
      setPredictions([]);
      return;
    }
    try {
      const { suggestions } =
        await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: inputValue,
          language: "en",
          // Restrict results strictly to the bounding box of the continental United States.
          // Coordinates define the southwest and northeast corners of the box:
          locationRestriction: {
            south: 24.396308,
            west: -125.0,
            north: 49.384358,
            east: -66.93457,
          },
        });

      setPredictions(suggestions || []);
    } catch (err) {
      console.error("Autocomplete fetch error:", err);
      setPredictions([]);
    }
  };

  const handleSelect = async (
    placeId: string,
    onChange: (val: string) => void
  ) => {
    const prediction = predictions.find(
      (p) => p.placePrediction?.placeId === placeId
    );
    if (!prediction) return;

    try {
      const place = prediction.placePrediction!.toPlace();
      await place.fetchFields({
        fields: [
          "formattedAddress",
          "viewport",
          "addressComponents",
          "location",
        ],
      });

      const formatted = place.formattedAddress || "";
      onChange(formatted);
      onPlaceSelect(place);
      setPredictions([]);
    } catch (err) {
      console.error("Place fetch error:", err);
    }
  };

  const commonInput = (
    value: string,
    onChange: (val: string) => void,
    onBlur?: () => void,
    ref?: React.Ref<any>,
    error?: string
  ) => (
    <div style={{ position: "relative" }} className="mb-3">
      <label htmlFor="place-input">Address</label>
      <input
        id="place-input"
        type="text"
        value={value}
        onChange={(e) => {
          const val = e.target.value;
          onChange(val);
          setInputValue(val);

          if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
          debounceTimeout.current = setTimeout(() => {
            fetchSuggestions(val);
          }, 300);
        }}
        onBlur={onBlur}
        ref={ref}
        placeholder="Enter an address"
        style={{
          width: "100%",
          padding: "10px",
          fontSize: "1rem",
          border: error ? "1px solid red" : "1px solid #ccc",
          borderRadius: "6px",
          boxSizing: "border-box",
        }}
        autoComplete="off"
      />
      {error && (
        <p style={{ color: "red", fontSize: "0.875rem", marginTop: "2px" }}>
          {error}
        </p>
      )}

      {predictions.length > 0 && (
        <ul
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #ccc",
            zIndex: 1000,
            margin: 0,
            padding: 0,
            listStyle: "none",
            maxHeight: "200px",
            overflowY: "auto",
            borderRadius: "4px",
          }}
        >
          {predictions.map((pred) => (
            <li
              key={pred.placePrediction?.placeId}
              onClick={() =>
                handleSelect(pred.placePrediction!.placeId, onChange)
              }
              style={{
                padding: "8px",
                cursor: "pointer",
                borderBottom: "1px solid #eee",
              }}
            >
              {pred.placePrediction!.text?.text ||
                pred.placePrediction!.mainText?.text ||
                "Unnamed place"}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  if (!control) {
    // Fallback uncontrolled input
    return commonInput(inputValue, (val) => setInputValue(val));
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) =>
        commonInput(
          field.value || "",
          field.onChange,
          field.onBlur,
          field.ref,
          fieldState.error?.message
        )
      }
    />
  );
}
